import { D0_FOLLOW_LOCALS_STORAGE_KEY } from 'src/constants/storageKeys.const';
import { twentyFourHoursAgo } from 'src/apps/DashboardFrame/utils/twentyFourHoursAgo';
import { getLocalStorageEntry, setLocalStorageEntry } from 'src/utils/webStorageUtils';

const REPEAT_WINDOW_MS = 2000;

type DayZeroFollowLocalsState = {
  userId: number;
  count: number;
  lastRecordedAt: number;
  seen: boolean;
};

const emptyState = (userId: number): DayZeroFollowLocalsState => ({
  userId,
  count: 0,
  lastRecordedAt: 0,
  seen: false,
});

const readState = (userId: number): DayZeroFollowLocalsState => {
  const raw = getLocalStorageEntry(D0_FOLLOW_LOCALS_STORAGE_KEY);
  if (!raw) return emptyState(userId);

  try {
    const parsed = JSON.parse(raw) as DayZeroFollowLocalsState;
    if (parsed.userId !== userId) return emptyState(userId);
    return parsed;
  } catch {
    return emptyState(userId);
  }
};

const writeState = (state: DayZeroFollowLocalsState) => {
  setLocalStorageEntry(D0_FOLLOW_LOCALS_STORAGE_KEY, JSON.stringify(state));
};

export const isDayZeroUser = (dateJoined: string | undefined, now = Date.now()): boolean => {
  if (!dateJoined) return false;
  const joinedAt = Date.parse(dateJoined);
  if (Number.isNaN(joinedAt)) return false;
  return twentyFourHoursAgo(joinedAt, now);
};

/**
 * Counts one completed Learn session for this user. A second call within two
 * seconds is ignored so a strict-mode remount of the same completion does not
 * count as another session.
 */
export const recordDayZeroLearnSession = (userId: number, now = Date.now()): number => {
  const state = readState(userId);
  if (now - state.lastRecordedAt < REPEAT_WINDOW_MS) return state.count;

  const next = { ...state, count: state.count + 1, lastRecordedAt: now };
  writeState(next);
  return next.count;
};

export const hasSeenFollowLocals = (userId: number): boolean => readState(userId).seen;

export const markFollowLocalsSeen = (userId: number): void => {
  writeState({ ...readState(userId), seen: true });
};
